/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @author Satyaki Bose
 * @file LateWorkDeadlineSelect.jsx
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { shift, offset } from '@floating-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FormControl,
  GlobalStyles,
  Select
} from '@mui/material';
import Icon from '../../icons/Icon';
import SettingsSelectDropdownIcon from './SettingsSelectDropdownIcon';
import { renderSettingsSelectMenuItem } from './SettingsSelectMenuItem';
import { getSettingsSelectMenuProps } from './styles/settingsSelectFieldStyles';
import { LATE_WORK_DEADLINE_TYPE } from './utils/assignmentSettingsConstants';
import {
  formatDateToStorage,
  formatMonthYear,
  parseDate
} from '../../../common/dateTimeUtils';
import {
  LateWorkDeadlineSelectStylesRoot,
  classes,
  globalStyles
} from './styles/lateWorkDeadlineSelectStyles';
import {
  formatDateMaskInput,
  formatInputDateFromStorage,
  isValidMaskedDateInput,
  parseMaskedDateInput
} from './utils/lateWorkCustomDateInputUtils';

const stopMenuListKeydown = (event) => {
  if (event.key === 'Escape') {
    return;
  }

  event.stopPropagation();
};

const isDatePickerInteraction = (event) => {
  const target = event?.target;
  if (!target?.closest) {
    return false;
  }

  return Boolean(
    target.closest('#react-datepicker-portal')
    || target.closest('.late-work-deadline-datepicker-popper')
    || target.closest('.react-datepicker-popper')
    || target.closest('.datePickerPortalWrapper')
    || target.closest('.react-datepicker')
  );
};

const stopPickerPointerEvent = (event) => {
  event.stopPropagation();
};

const formatCustomDateDisplay = (dateStr, fallbackLabel = 'Custom date') => {
  const date = parseDate(dateStr);
  if (!date) {
    return fallbackLabel;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const LateWorkCustomDateField = React.forwardRef(({
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  placeholder,
  disabled,
  hasError,
  calendarSlot
}, ref) => {
  const rowClassName = [
    classes.dateInputRow,
    hasError ? classes.dateInputRowInvalid : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={rowClassName}>
      <input
        ref={ref}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        placeholder={placeholder}
        disabled={disabled}
        className={classes.dateInputField}
        aria-label={placeholder}
        aria-invalid={hasError || undefined}
        inputMode="numeric"
        maxLength={10}
        autoComplete="off"
      />
      <div className={classes.dateInputSeparator} aria-hidden="true" />
      <div className={classes.calendarPickerSlot}>
        {calendarSlot}
      </div>
    </div>
  );
});

LateWorkCustomDateField.displayName = 'LateWorkCustomDateField';

LateWorkCustomDateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  calendarSlot: PropTypes.node
};

LateWorkCustomDateField.defaultProps = {
  value: '',
  onChange: null,
  onBlur: null,
  onFocus: null,
  onKeyDown: null,
  placeholder: '',
  disabled: false,
  hasError: false,
  calendarSlot: null
};

const CalendarIconTrigger = React.forwardRef(({
  onClick,
  onOpenCalendar,
  onKeyDown,
  disabled
}, ref) => (
  <button
    ref={ref}
    type="button"
    className={classes.calendarIconButton}
    onMouseDown={(event) => event.stopPropagation()}
    onKeyDown={onKeyDown}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onOpenCalendar?.(event);
      onClick?.(event);
    }}
    disabled={disabled}
    aria-label="Open calendar"
  >
    <Icon name="CalendarIcon" />
  </button>
));

CalendarIconTrigger.displayName = 'CalendarIconTrigger';

CalendarIconTrigger.propTypes = {
  onClick: PropTypes.func,
  onOpenCalendar: PropTypes.func,
  onKeyDown: PropTypes.func,
  disabled: PropTypes.bool
};

CalendarIconTrigger.defaultProps = {
  onClick: null,
  onOpenCalendar: null,
  onKeyDown: null,
  disabled: false
};

const LateWorkDeadlineSelect = ({
  value,
  options,
  customDateValue,
  disabled,
  className,
  showCustomDatePanel,
  config,
  textInfo,
  onDeadlineChange,
  onCustomDateChange,
  onCustomDateApply
}) => {
  const showSelectedCheckIcon = config?.showSelectedCheckIcon !== false;
  const customPanelActiveRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCustomPanelActive, setIsCustomPanelActive] = useState(false);
  const [draftDate, setDraftDate] = useState('');
  const [draftInputText, setDraftInputText] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [minSelectableDate, setMinSelectableDate] = useState(() => new Date());

  const visibleOptions = useMemo(
    () => (showCustomDatePanel
      ? options
      : options.filter((option) => option.value !== LATE_WORK_DEADLINE_TYPE.CUSTOM)),
    [options, showCustomDatePanel]
  );

  const selectValue = isCustomPanelActive
    ? LATE_WORK_DEADLINE_TYPE.CUSTOM
    : (value || '');

  const customDateOptionLabel = useMemo(
    () => options.find((option) => option.value === LATE_WORK_DEADLINE_TYPE.CUSTOM)?.label
      || '',
    [options]
  );

  const renderSelectValue = useCallback((selectedValue) => {
    if (selectedValue === LATE_WORK_DEADLINE_TYPE.CUSTOM) {
      return formatCustomDateDisplay(customDateValue, customDateOptionLabel);
    }

    return visibleOptions.find((option) => option.value === selectedValue)?.label || selectedValue;
  }, [customDateValue, customDateOptionLabel, visibleOptions]);

  const selectedDate = parseDate(draftDate);
  const openToDate = selectedDate || new Date();
  const canApply = isValidMaskedDateInput(draftInputText, { minDate: minSelectableDate });
  const hasInputError = Boolean(draftInputText)
    && !isValidMaskedDateInput(draftInputText, { minDate: minSelectableDate });

  const syncCustomPanelState = useCallback((active, date) => {
    customPanelActiveRef.current = active;
    setIsCustomPanelActive(active);
    setDraftDate(date || '');
    setDraftInputText(formatInputDateFromStorage(date));
  }, []);

  useEffect(() => {
    if (menuOpen) {
      setMinSelectableDate(new Date());
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      setPickerOpen(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    if (value === LATE_WORK_DEADLINE_TYPE.CUSTOM && showCustomDatePanel) {
      syncCustomPanelState(true, customDateValue || '');
    }
  }, [menuOpen, value, customDateValue, showCustomDatePanel, syncCustomPanelState]);

  const handleOpen = () => {
    setMenuOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick' && isDatePickerInteraction(event)) {
      return;
    }

    if (customPanelActiveRef.current || isCustomPanelActive) {
      if (
        reason === 'backdropClick'
        || reason === 'escapeKeyDown'
        || reason === 'toggleInput'
        || reason === 'tabKeyDown'
      ) {
        syncCustomPanelState(false, customDateValue || '');
        setPickerOpen(false);
        setMenuOpen(false);

        return;
      }

      if (reason === 'selectOption') {
        customPanelActiveRef.current = false;
        setIsCustomPanelActive(false);
        setPickerOpen(false);
        setMenuOpen(false);

        return;
      }

      return;
    }

    setMenuOpen(false);
  };

  const handleChange = (event) => {
    const nextValue = event.target.value;
    const isKnownOption = visibleOptions.some((option) => option.value === nextValue);

    if (!nextValue || !isKnownOption) {
      return;
    }

    if (nextValue === LATE_WORK_DEADLINE_TYPE.CUSTOM && showCustomDatePanel) {
      syncCustomPanelState(true, customDateValue || '');
      setMenuOpen(true);

      return;
    }

    syncCustomPanelState(false, customDateValue || '');
    setPickerOpen(false);
    onDeadlineChange?.(nextValue);
    setMenuOpen(false);
  };

  const handleTextChange = (event) => {
    const masked = formatDateMaskInput(event.target.value);
    setDraftInputText(masked);

    const parsed = parseMaskedDateInput(masked, { minDate: minSelectableDate });
    setDraftDate(parsed || '');
  };

  const handleTextFocus = () => {
    setDraftInputText((current) => formatDateMaskInput(current));
  };

  const handleTextBlur = () => {
    const parsed = parseMaskedDateInput(draftInputText, { minDate: minSelectableDate });

    if (parsed) {
      setDraftDate(parsed);
      setDraftInputText(formatInputDateFromStorage(parsed));

      return;
    }

    setDraftInputText(formatDateMaskInput(draftInputText));
  };

  const handleDateChange = (date) => {
    const formattedDate = formatDateToStorage(date);
    setDraftDate(formattedDate || '');
    setDraftInputText(formatInputDateFromStorage(formattedDate));
    setPickerOpen(false);
  };

  const handleApply = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const dateToCommit = parseMaskedDateInput(draftInputText, { minDate: minSelectableDate });

    if (!dateToCommit) {
      return;
    }

    customPanelActiveRef.current = false;
    setIsCustomPanelActive(false);
    setDraftDate(dateToCommit);
    setDraftInputText(formatInputDateFromStorage(dateToCommit));
    setPickerOpen(false);
    onDeadlineChange?.(LATE_WORK_DEADLINE_TYPE.CUSTOM);
    onCustomDateChange?.(dateToCommit);
    onCustomDateApply?.(dateToCommit);
    setMenuOpen(false);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
  }) => (
    <div className={classes.calendarHeader}>
      <button
        type="button"
        onMouseDown={stopPickerPointerEvent}
        onClick={(event) => {
          stopPickerPointerEvent(event);
          decreaseMonth();
        }}
        className={`${classes.calendarNavButton} react-datepicker-ignore-onclickoutside`}
        aria-label="Previous month"
        disabled={prevMonthButtonDisabled}
      >
        <Icon name="CaretPrevIcon" />
      </button>
      <span className={classes.calendarMonthYearLabel}>
        {formatMonthYear(date)}
      </span>
      <button
        type="button"
        onMouseDown={stopPickerPointerEvent}
        onClick={(event) => {
          stopPickerPointerEvent(event);
          increaseMonth();
        }}
        className={`${classes.calendarNavButton} react-datepicker-ignore-onclickoutside`}
        aria-label="Next month"
        disabled={nextMonthButtonDisabled}
      >
        <Icon name="CaretNextIcon" />
      </button>
    </div>
  );

  const popperContainer = useCallback(
    ({ children }) => (
      <LateWorkDeadlineSelectStylesRoot className={classes.datePickerPortalWrapper}>
        {children}
      </LateWorkDeadlineSelectStylesRoot>
    ),
    []
  );

  const handleOpenCalendar = useCallback(() => {
    if (!disabled) {
      setPickerOpen(true);
    }
  }, [disabled]);

  const selectClassName = [
    classes.selectRoot,
    disabled ? classes.selectDisabled : ''
  ].filter(Boolean).join(' ');

  const applyLabel = textInfo?.customDateApplyLabel || 'Apply';
  const placeholderText = textInfo?.customDatePlaceholder || 'mm/dd/yyyy';

  return (
    <LateWorkDeadlineSelectStylesRoot className={`${classes.wrapper} ${className || ''}`.trim()}>
      <GlobalStyles styles={globalStyles} />
      <FormControl variant="outlined" fullWidth disabled={disabled}>
        <Select
          open={menuOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          value={selectValue}
          onChange={handleChange}
          renderValue={renderSelectValue}
          className={selectClassName}
          IconComponent={SettingsSelectDropdownIcon}
          MenuProps={getSettingsSelectMenuProps(classes.menuPaper, {
            autoFocus: false,
            disableAutoFocusItem: true,
            PaperProps: {
              component: LateWorkDeadlineSelectStylesRoot,
              style: { overflow: 'visible', maxHeight: 'none' }
            }
          })}
        >
          {visibleOptions.map((option) => renderSettingsSelectMenuItem({
            option,
            selectedValue: selectValue,
            classes,
            showSelectedCheckIcon,
            menuItemProps: {
              onMouseDown:
                option.value === LATE_WORK_DEADLINE_TYPE.CUSTOM && showCustomDatePanel
                  ? (event) => event.preventDefault()
                  : undefined
            }
          }))}

          {isCustomPanelActive && showCustomDatePanel ? (
            <li
              role="presentation"
              className={classes.customPanelItem}
              onMouseDown={(event) => event.preventDefault()}
            >
              <div className={classes.customPanel}>
                <div className={classes.datePickerContainer}>
                  <LateWorkCustomDateField
                    value={draftInputText}
                    onChange={handleTextChange}
                    onBlur={handleTextBlur}
                    onFocus={handleTextFocus}
                    onKeyDown={stopMenuListKeydown}
                    placeholder={placeholderText}
                    disabled={disabled}
                    hasError={hasInputError}
                    calendarSlot={(
                      <DatePicker
                        selected={selectedDate}
                        onChange={handleDateChange}
                        disabled={disabled}
                        open={pickerOpen}
                        onClickOutside={(event) => {
                          if (isDatePickerInteraction(event)) {
                            return;
                          }

                          setPickerOpen(false);
                        }}
                        preventOpenOnFocus
                        shouldCloseOnSelect
                        showPopperArrow={false}
                        popperPlacement="bottom-end"
                        popperProps={{
                          middleware: [
                            offset(8),
                            shift({
                              padding: 10,
                              boundary: document.body,
                              crossAxis: true
                            })
                          ]
                        }}
                        popperClassName={`${classes.datePickerPopper} late-work-deadline-datepicker-popper`}
                        portalId="react-datepicker-portal"
                        popperContainer={popperContainer}
                        openToDate={openToDate}
                        calendarStartDay={0}
                        renderCustomHeader={renderCustomHeader}
                        minDate={minSelectableDate}
                        customInput={(
                          <CalendarIconTrigger
                            disabled={disabled}
                            onOpenCalendar={handleOpenCalendar}
                          />
                        )}
                      />
                    )}
                  />
                </div>
                <button
                  type="button"
                  className={classes.applyButton}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={handleApply}
                  disabled={disabled || !canApply}
                >
                  {applyLabel}
                </button>
              </div>
            </li>
          ) : null}
        </Select>
      </FormControl>
    </LateWorkDeadlineSelectStylesRoot>
  );
};

LateWorkDeadlineSelect.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  customDateValue: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showCustomDatePanel: PropTypes.bool,
  config: PropTypes.shape({
    showSelectedCheckIcon: PropTypes.bool
  }),
  textInfo: PropTypes.object,
  onDeadlineChange: PropTypes.func,
  onCustomDateChange: PropTypes.func,
  onCustomDateApply: PropTypes.func
};

LateWorkDeadlineSelect.defaultProps = {
  value: '',
  options: [],
  customDateValue: '',
  disabled: false,
  className: '',
  showCustomDatePanel: true,
  config: {},
  textInfo: {},
  onDeadlineChange: null,
  onCustomDateChange: null,
  onCustomDateApply: null
};

export default LateWorkDeadlineSelect;
